import { GameState, gameStates } from './../helpers/types';

import ls from 'localstorage-slim';
import type { FolderApi } from 'tweakpane';
import { DevTools } from '../utils/devTools';
import { SpineLayout } from '@pixi/spine-layout';
import type { Application } from 'pixi.js';
import { EventEmitter } from './EventEmitter';
import { config } from '../settings/config';

let devTools: DevTools;
let layout: SpineLayout | undefined;

export function addCheats(app: Application, spineLayout: SpineLayout) {
    devTools = new DevTools({
        app,
        gameName: APP_NAME,
        gameVersion: `${APP_VERSION} (${APP_MODE})`,
    });

    layout = spineLayout;

    // this.addBECheats();
    addSpineCheats();
    addStateCheats();
}

export function addSpineCheats() {
    const editorFolder = devTools.addFolder({
        title: 'Spines',
        expanded: true,
    });

    const stopAllButton = editorFolder.addButton({ title: 'stop all' });

    stopAllButton.on('click', async () => {
        console.log('stop all animations');
        await layout?.stopAll();
    });

    const availableAnimationsFolder = editorFolder.addFolder({
        title: 'Animations',
        expanded: ls.get('spine-animations-expanded') ?? true,
    });

    availableAnimationsFolder.on('fold', () => {
        ls.set('spine-animations-expanded', availableAnimationsFolder?.expanded);
    });

    const availableAnimationsStatesFolder = editorFolder.addFolder({
        title: 'States',
        expanded: ls.get('spine-animations-states-expanded') ?? true,
    });

    availableAnimationsStatesFolder.on('fold', () => {
        ls.set('spine-animations-states-expanded', availableAnimationsStatesFolder?.expanded);
    });

    const stateAnimationsFolders: Map<string, FolderApi> = new Map();

    layout?.getAnimations().forEach((animation) => {
        if (animation.includes('/')) {
            const [state, animationName] = animation.split('/');
            const stateFolder = stateAnimationsFolders.get(state);

            if (stateFolder) {
                addAnimationButton(stateFolder, animationName, animation);
            } else {
                const folder = availableAnimationsFolder.addFolder({
                    title: state,
                    expanded: ls.get(`spine-animations-states-expanded_${state}`) ?? true,
                });

                folder.on('fold', () => {
                    ls.set(`spine-animations-states-expanded_${state}`, folder?.expanded);
                });
                stateAnimationsFolders.set(state, folder);

                addAnimationButton(folder, animationName, animation);
            }
        } else {
            addAnimationButton(availableAnimationsFolder, animation, animation);
        }
    });

    layout?.getAnimationsStates().forEach((state) => {
        availableAnimationsStatesFolder.addButton({ title: state }).on('click', async () => {
            console.log(`STATE start: ${state}`);

            await layout?.playState(state);

            console.log(`STATE end: ${state}`);
        });
    });
}

function addAnimationButton(folder: FolderApi, animationName: string, animation: string) {
    folder.addButton({ title: animationName }).on('click', async () => {
        console.log(`start: ${animation}`);

        const activeAnimations = layout?.getActiveAnimations() || [];
        const loopingAnimations = layout?.getLoopingAnimations() || [];
        console.log(`Active animations: ${activeAnimations.join(', ')}`);
        console.log(`Looping animations: ${loopingAnimations.join(', ')}`);

        await layout?.playAnimationByName(animation);

        console.log(`end: ${animation}`);
    });
}

let horses = [
    {
        name: 'Horse-1',
        multiplier: 1,
        weight: 1,
        distance: 1,
        position: 1,
        finished: false,
    },
    {
        name: 'Horse-2',
        multiplier: 1,
        weight: 1,
        distance: 1,
        position: 2,
        finished: false,
    },
    {
        name: 'Horse-3',
        multiplier: 1,
        weight: 1,
        distance: 1,
        position: 3,
        finished: false,
    },
    {
        name: 'Horse-4',
        multiplier: 1,
        weight: 1,
        distance: 1,
        position: 4,
        finished: false,
    },
    {
        name: 'Horse-5',
        multiplier: 1,
        weight: 1,
        distance: 1,
        position: 1,
        finished: false,
    },
    {
        name: 'Horse-6',
        multiplier: 1,
        weight: 1,
        distance: 1,
        position: 1,
        finished: false,
    },
    {
        name: 'Horse-7',
        multiplier: 1,
        weight: 1,
        distance: 1,
        position: 1,
        finished: false,
    },
    {
        name: 'Horse-8',
        multiplier: 1,
        weight: 1,
        distance: 1,
        position: 1,
        finished: false,
    },
]
    .sort(() => Math.random() - 0.5)
    .slice(0, 6)
    .map((horse, index) => ({
        ...horse,
        position: index + 1,
    }));

const tickData = {
    raceId: 'RACE-c68ab6a9-5c09-4914-99a8-e23206233fde',
    raceTick: 1,
    type: 'horses:tick',
    horses,
};

let interval: NodeJS.Timeout;

function playPlaceBetPhase(): Promise<void> {
    return new Promise((resolve) => {
        let raceTick = 0;

        clearInterval(interval);
        interval = setInterval(() => {
            if (raceTick >= config.ticksAmount.placeBet) {
                clearInterval(interval);
                resolve();
                return;
            }

            raceTick += 1;

            EventEmitter.emit('horses:update', {
                ...tickData,
                gameState: 'PLACE_BET',
                raceTick,
                horses,
            });
        }, 100);
    });
}

function playNoMoreBetsPhase(): Promise<void> {
    return new Promise((resolve) => {
        let raceTick = 0;

        clearInterval(interval);
        interval = setInterval(() => {
            if (raceTick >= config.ticksAmount.noMoreBets) {
                clearInterval(interval);
                resolve();
                return;
            }

            raceTick += 1;

            EventEmitter.emit('horses:update', {
                ...tickData,
                gameState: 'NO_MORE_BETS',
                raceTick,
                horses,
            });
        }, 100);
    });
}

function playRacePhase(): Promise<void> {
    return new Promise((resolve) => {
        let raceTick = 0;

        clearInterval(interval);
        interval = setInterval(() => {
            if (raceTick >= config.ticksAmount.race) {
                clearInterval(interval);
                resolve();
                return;
            }

            raceTick += 1;

            horses = horses.map((horse) => {
                if (horse.finished) {
                    return horse;
                }

                const runProgressModificator = 45;
                const randomProgress = Math.floor(Math.random() * runProgressModificator);

                horse.distance = horse.distance + randomProgress;

                if (horse.distance >= config.maxDistance) {
                    horse.distance = config.maxDistance;
                    horse.finished = true;
                }

                return horse;
            });

            horses = horses.map((horse) => {
                horse.position =
                    horses
                        .filter((h) => h.distance >= horse.distance)
                        .sort((a, b) => b.distance - a.distance)
                        .findIndex((h) => h.name === horse.name) + 1;

                return horse;
            });

            EventEmitter.emit('horses:update', {
                ...tickData,
                gameState: 'RACE',
                horses,
                raceTick,
            });
        }, 100);
    });
}

function playCelebrationPhase(): Promise<void> {
    return new Promise((resolve) => {
        let raceTick = 0;

        horses = horses.map((horse, index) => ({
            ...horse,
            time: `${index + 1}${index + 1}:${index + 1}${index + 1}`,
        }));

        clearInterval(interval);
        interval = setInterval(() => {
            if (raceTick >= config.ticksAmount.celebration) {
                clearInterval(interval);
                resolve();
                return;
            }

            raceTick += 1;

            EventEmitter.emit('horses:update', {
                ...tickData,
                horses,
                gameState: 'CELEBRATION',
                raceTick: raceTick,
            });
        }, 100);
    });
}

async function playState(state: GameState) {
    switch (state) {
        case 'PLACE_BET':
            await playPlaceBetPhase();
            break;
        case 'NO_MORE_BETS':
            await playNoMoreBetsPhase();
            break;
        case 'RACE':
            await playRacePhase();
            break;
        case 'CELEBRATION':
            await playCelebrationPhase();
            break;
    }
}

async function addStateCheats() {
    const stateCheatsFolder = devTools.addFolder({
        title: 'State cheats',
        expanded: true,
    });

    stateCheatsFolder
        .addBinding({ initState: ls.get('init-state') || '' }, 'initState', {
            options: Object.fromEntries(
                ['no init state', ...gameStates].map((event) => [event, event]),
            ),
        })
        .on('change', ({ value }) => {
            if (value === 'no init state') {
                ls.remove('init-state');

                return;
            }

            ls.set('init-state', value);
        });

    if (ls.get('init-state')) {
        playState(ls.get('init-state') as GameState);
    }

    stateCheatsFolder.addButton({ title: 'Place Bet' }).on('click', playPlaceBetPhase);
    stateCheatsFolder.addButton({ title: 'No More Bets' }).on('click', playNoMoreBetsPhase);
    stateCheatsFolder.addButton({ title: 'Race' }).on('click', playRacePhase);
    stateCheatsFolder.addButton({ title: 'Celebration' }).on('click', playCelebrationPhase);
}
